'use client';

import { useTranslation } from '@/app/i18n/client';
import AppContext from '@/components/AppContext';
import Copyright from '@/components/Copyright';
import LocaleSwitcher from '@/components/LocaleSwitcher';
import Logo from '@/components/Logo';
import ThemeSwitcher from '@/components/ThemeSwitcher';
import useCryptoSupport from '@/hooks/useCryptoSupport';
import useSecureContext from '@/hooks/useSecureContext';
import EnhancedEncryptionIcon from '@mui/icons-material/EnhancedEncryption';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import NoEncryptionIcon from '@mui/icons-material/NoEncryption';
import RemoveModeratorIcon from '@mui/icons-material/RemoveModerator';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Container from '@mui/material/Container';
import Fab from '@mui/material/Fab';
import Snackbar from '@mui/material/Snackbar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Zoom from '@mui/material/Zoom';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import Link from 'next/link';
import { useContext, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import { TransitionGroup } from 'react-transition-group';
import Form from './Form';

export default function Report() {
  const app = useContext(AppContext);
  const { t } = useTranslation(app.i18n.lng);
  const secureContext = useSecureContext();
  const cryptoSupport = useCryptoSupport();
  const [settings, setSettings] = useState<any>();
  const scrollTrigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  /**
   * Fetch tenant settings
   */
  useEffect(() => {
    let isMounted = true;

    fetch('/api/settings')
      .then((response) => {
        if (response.ok) {
          return response.json();
        }
        throw new Error('Network response was not ok.');
      })
      .then((data) => {
        console.log(data);
        if (isMounted) {
          setSettings(data.settings);
        }
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const [error, setError] = useState('');

  const scrollToTop = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (
      (event.target as HTMLDivElement).ownerDocument || document
    ).querySelector('#back-to-top-anchor');

    if (anchor) {
      anchor.scrollIntoView({
        block: 'center',
      });
    }
  };

  const handleClose = () => {
    setError('');
  };

  return (
    <>
      <AppBar
        component="nav"
        position="sticky"
        elevation={scrollTrigger ? 4 : 0}
      >
        <Container maxWidth="md">
          <Toolbar disableGutters>
            <Link href="/">
              <Logo height={24} margin={'0.5rem'} />
            </Link>

            <Box sx={{ flexGrow: 1, display: 'flex' }}></Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                flexGrow: 0,
                flexWrap: 'nowrap',
              }}
            >
              <LocaleSwitcher size="small" sx={{ mr: 1 }} />
              <ThemeSwitcher menu size="small" />
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Snackbar
        open={!!error}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Container maxWidth="md">
        <LocalizationProvider
          dateAdapter={AdapterLuxon}
          adapterLocale={app.i18n.lng}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'start',
              alignItems: 'center',
              minHeight: '100vh',
            }}
          >
            <Box
              sx={{
                my: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" color="error">
                To nie jest miejsce do zgłaszania nagłych incydentów lub
                wypadków.
              </Typography>
              <Typography variant="subtitle1">
                Nie korzystaj z tej witryny do zgłaszania zdarzeń stanowiących
                bezpośrednie zagrożenie życia lub mienia. Raporty przesłane za
                pośrednictwem tej usługi mogą nie otrzymać natychmiastowej
                odpowiedzi. Jeśli potrzebujesz pomocy w nagłych wypadkach,
                skontaktuj się z lokalnymi komórkami/służbami.
              </Typography>
            </Box>

            <Box
              sx={{
                my: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" id="back-to-top-anchor">
                W celu wysłania zgłoszenia prosimy o wypełnienie poniższego
                formularza.
              </Typography>
            </Box>

            <TransitionGroup>
              {!secureContext ? (
                <Collapse>
                  <Box
                    sx={{
                      mt: 'auto',
                      mb: 6,
                    }}
                  >
                    <Alert icon={<RemoveModeratorIcon />} severity="error">
                      <AlertTitle>
                        {t(
                          'report.security.insecure.title',
                          'Your connection is not secure'
                        )}
                      </AlertTitle>
                      <Typography variant="body1" sx={{ my: 2 }}>
                        {t(
                          'report.security.insecure.body.1',
                          'We have noticed that your current connection or device does not meet the security standards.'
                        )}
                      </Typography>
                      <Typography variant="body1" sx={{ my: 2 }}>
                        {t(
                          'report.security.insecure.body.2',
                          'This means that the information you enter and send from this location may not be fully protected from interception or access by unauthorized third parties during transmission.'
                        )}
                      </Typography>
                      <Typography variant="body1" sx={{ my: 2 }}>
                        <Trans i18nKey="report.security.insecure.body.3" t={t}>
                          <strong>
                            Therefore, we strongly advise against completing and
                            sending a submission, especially if it contains
                            sensitive or private data, using this device or
                            connection.
                          </strong>{' '}
                          To protect your information, please use a device and
                          network connection that assures appropriate security
                          standards (this is typically an HTTPS connection on a
                          trusted site and device).
                        </Trans>
                      </Typography>
                    </Alert>
                  </Box>
                </Collapse>
              ) : (
                !cryptoSupport.pending && (
                  <Collapse>
                    <Box
                      sx={{
                        mt: 'auto',
                        mb: 6,
                      }}
                    >
                      {cryptoSupport.supported ? (
                        <Alert
                          icon={<EnhancedEncryptionIcon />}
                          severity="success"
                        >
                          <AlertTitle>
                            {t(
                              'report.encryption.e2e.supported.title',
                              'Data will be encrypted in your browser'
                            )}
                          </AlertTitle>
                          {t(
                            'report.encryption.e2e.supported.body',
                            'Every bit of information that you provide below will be encrypted in your browser before being sent to us. Only designated personnel at the destination company will be able to decrypt and read your report.'
                          )}
                        </Alert>
                      ) : (
                        <Alert icon={<NoEncryptionIcon />} severity="warning">
                          <AlertTitle>
                            {t(
                              'report.encryption.e2e.backend.title',
                              'Your browser does not support encryption'
                            )}
                          </AlertTitle>

                          <Typography variant="h6" sx={{ my: 2 }}>
                            {t(
                              'report.encryption.e2e.backend.body.head',
                              'Important Information Regarding the Security of Your Data'
                            )}
                          </Typography>
                          <Typography variant="body1">
                            {t(
                              'report.encryption.e2e.backend.body.intro',
                              'Your browser does not support direct data encryption before sending. To ensure the confidentiality of your submission:'
                            )}
                          </Typography>
                          <ol>
                            <Typography variant="body1" component="li">
                              {t(
                                'report.encryption.e2e.backend.body.1',
                                'The data you enter will be securely transmitted (via an encrypted connection) to our server.'
                              )}
                            </Typography>
                            <Typography variant="body1" component="li">
                              {t(
                                'report.encryption.e2e.backend.body.2',
                                'On our server, your submission will be immediately encrypted in such a way that only authorized personnel of the service recipient can decrypt it.'
                              )}
                            </Typography>
                            <Typography variant="body1" component="li">
                              {t(
                                'report.encryption.e2e.backend.body.3',
                                'We emphasize that your data will not be stored by us. It will only be used to create this encrypted copy for the recipient.'
                              )}
                            </Typography>
                          </ol>
                          <Typography variant="body1">
                            {t(
                              'report.encryption.e2e.backend.body.after',
                              `Through this process, despite your browser's limitations, your information will remain protected.`
                            )}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{ fontStyle: 'italic' }}
                          >
                            <Box
                              sx={{
                                mt: 2,
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'start',
                                alignItems: 'center',
                              }}
                            >
                              <Box sx={{ mr: 2, flexGrow: 0 }}>
                                <TipsAndUpdatesIcon />
                              </Box>
                              <Box sx={{ flexGrow: 1 }}>
                                {t(
                                  'report.encryption.e2e.deprecatedBrowser',
                                  'Please consider updating your browser to a more recent version.'
                                )}
                              </Box>
                            </Box>
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </Collapse>
                )
              )}
            </TransitionGroup>

            <Box
              sx={{
                my: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'stretch',
              }}
            >
              <Form setError={setError} settings={settings} />
            </Box>

            <Zoom in={scrollTrigger}>
              <Box
                onClick={scrollToTop}
                role="presentation"
                sx={{ position: 'fixed', bottom: 16, right: 16 }}
              >
                <Fab size="small">
                  <KeyboardArrowUpIcon />
                </Fab>
              </Box>
            </Zoom>

            <Box
              sx={{
                mt: 'auto',
              }}
            >
              <Box sx={{ mt: 2, mb: 6 }}>
                <Copyright />
              </Box>
            </Box>
          </Box>
        </LocalizationProvider>
      </Container>
    </>
  );
}
